import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, LeaderBoardCell, LeaderBoardLabel, LeaderBoardRow } from '@dreamteam-monorepo/ui';
import { useNavigate } from 'react-router';
import { LEAGUE_OVERALL_PERIOD_VALUE } from '@/stores/LeagueDetailStore';
import type { LeaderboardEntry, LeaderboardPeriod } from '@/lib/network';
import { useLeaderboardInfiniteQuery } from '@/hooks/useLeaderboardQuery';

const LEADERBOARD_PAGE_SIZE = 50;
const VIEWABLE_STATUSES = new Set(['locked', 'live', 'completed', 'finalised']);

const isViewableStatus = (status: string | undefined): boolean =>
  !!status && VIEWABLE_STATUSES.has(status);

const SkeletonRow: FC = () => (
  <div className="h-[52px] md:h-[62px] border-b border-[#eae9fd] bg-white animate-pulse" />
);

function toLeaderBoardRow(
  entry: LeaderboardEntry,
  showAverage: boolean,
  isOverallSelected: boolean,
  selectedPeriodId: string,
  selectedPeriodStatus: string | undefined,
  latestViewablePeriodId: string | undefined,
  navigate: ReturnType<typeof useNavigate>,
  variant: LeaderBoardRow['variant'] = 'default'
): LeaderBoardRow {
  let targetGameweekId: string | undefined;
  let clickable = false;

  if (isOverallSelected) {
    targetGameweekId = latestViewablePeriodId;
    clickable = !!latestViewablePeriodId;
  } else {
    targetGameweekId = selectedPeriodId;
    clickable = isViewableStatus(selectedPeriodStatus);
  }

  const row: LeaderBoardRow = {
    variant,
    rank: String(entry.team_rank),
    teamId: entry.team_id,
    teamName: entry.team_name,
    managerName: entry.user_display_name,
    average: showAverage ? entry.average_points.toFixed(1) : '-',
    gameweekPoints: String(Math.round(entry.gameweek_points ?? entry.matchday_points ?? 0)),
    total: String(Math.round(entry.total_points)),
    gameweekId: targetGameweekId,
    leaderboardType: isOverallSelected ? 'overall' : 'per_gameweek',
    pageType: 'readonly',
  };

  if (clickable) {
    row.onCellClick = ({ teamId, gameweekId, leaderboardType, pageType }) => {
      const params = new URLSearchParams();
      if (teamId) params.set('teamId', teamId);
      if (gameweekId) params.set('gameweekId', gameweekId);
      if (leaderboardType) params.set('leaderboardType', leaderboardType);
      if (pageType) params.set('pageType', pageType);
      navigate(`/my-team?${params.toString()}`);
    };
  }

  return row;
}

export interface LeagueStandingsTableProps {
  leagueId: string;
  tournamentCalendarId: string | null;
  periodType: 'gameweek' | 'matchday';
  selectedPeriodId: string;
  pointsColumnLabel: string;
  leaderboardPeriods: LeaderboardPeriod[];
}

/**
 * League standings, paginated via `useLeaderboardInfiniteQuery` and grown as
 * the sentinel scrolls into view. The current user's row (identified from
 * the API's own `user_position`, scoped server-side to the authenticated
 * request) is pinned to a sticky header duplicate whenever it scrolls out
 * of view.
 */
export const LeagueStandingsTable: FC<LeagueStandingsTableProps> = ({
  leagueId,
  tournamentCalendarId,
  periodType,
  selectedPeriodId,
  pointsColumnLabel,
  leaderboardPeriods,
}) => {
  const navigate = useNavigate();
  const isOverallSelected = selectedPeriodId === LEAGUE_OVERALL_PERIOD_VALUE;

  const selectedPeriodStatus = useMemo(() => {
    if (isOverallSelected) return undefined;
    return leaderboardPeriods.find(p => p.id === selectedPeriodId)?.status;
  }, [leaderboardPeriods, selectedPeriodId, isOverallSelected]);

  const latestViewablePeriodId = useMemo(() => {
    const viewablePeriods = leaderboardPeriods.filter(p => isViewableStatus(p.status));
    if (viewablePeriods.length === 0) return undefined;
    return viewablePeriods[viewablePeriods.length - 1].id;
  }, [leaderboardPeriods]);

  const leaderboardParams = tournamentCalendarId
    ? {
        tournament_calendar: tournamentCalendarId,
        type: periodType,
        league_id: leagueId,
        limit: LEADERBOARD_PAGE_SIZE,
        ...(!isOverallSelected && periodType === 'matchday'
          ? { upto_matchday_id: selectedPeriodId }
          : {}),
        ...(!isOverallSelected && periodType === 'gameweek'
          ? { upto_gameweek_id: selectedPeriodId }
          : {}),
      }
    : null;

  const {
    data: leaderboardPages,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLeaderboardInfiniteQuery(leaderboardParams);

  const pages = leaderboardPages?.pages.map(p => p.data) ?? [];
  const entries: LeaderboardEntry[] = useMemo(() => pages.flatMap(page => page.entries), [pages]);
  const userPosition = pages.find(page => page.user_position)?.user_position ?? null;

  const rows: LeaderBoardRow[] = entries.map(entry =>
    toLeaderBoardRow(
      entry,
      isOverallSelected,
      isOverallSelected,
      selectedPeriodId,
      selectedPeriodStatus,
      latestViewablePeriodId,
      navigate
    )
  );

  let myRowIndex = -1;
  if (userPosition) {
    // Match on team id, not rank: `user_position` is ranked separately from the
    // list and can disagree with it, which would overwrite another team's row
    // and show the user twice. When the team is listed, the list's row wins.
    const listIdx = entries.findIndex(entry => entry.team_id === userPosition.team_id);
    const myEntry = listIdx === -1 ? userPosition : entries[listIdx];
    const userRow = toLeaderBoardRow(
      myEntry,
      isOverallSelected,
      isOverallSelected,
      selectedPeriodId,
      selectedPeriodStatus,
      latestViewablePeriodId,
      navigate,
      myEntry.team_rank === 1 ? 'winner' : 'myScore'
    );
    if (listIdx === -1) {
      rows.unshift(userRow);
      myRowIndex = 0;
    } else {
      rows[listIdx] = userRow;
      myRowIndex = listIdx;
    }
  }

  // ── Infinite scroll: fetch the next page once the sentinel enters view ──
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ── Sticky "my row": duplicate it above the fold once it scrolls out ──
  const myRowRef = useRef<HTMLDivElement>(null);
  const [isMyRowVisible, setIsMyRowVisible] = useState(true);
  useEffect(() => {
    const el = myRowRef.current;
    if (!el || myRowIndex === -1) {
      setIsMyRowVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsMyRowVisible(entry.isIntersecting),
      {
        threshold: 0,
      }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [myRowIndex, entries]);

  const myRow = myRowIndex !== -1 ? rows[myRowIndex] : null;

  if (isLoading || !tournamentCalendarId) {
    return (
      <>
        <LeaderBoardLabel
          showAverage={isOverallSelected}
          showGameweekPoints
          gameweekPointsLabel={pointsColumnLabel}
        />
        {Array.from({ length: 10 }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </>
    );
  }

  if (isError) {
    return (
      <div className="flex w-full items-center justify-center py-16 text-red-500">
        Failed to load the leaderboard.
      </div>
    );
  }

  return (
    <>
      {!isOverallSelected && !isViewableStatus(selectedPeriodStatus) && (
        <div className="px-[--padding-md] pb-[--padding-sm]">
          <Alert
            title="Team view not available"
            description="Team selections are only visible for locked, live or finalised gameweeks."
            state="default"
          />
        </div>
      )}
      <div className="sticky top-0 z-10">
        <LeaderBoardLabel
          showAverage={isOverallSelected}
          showGameweekPoints
          gameweekPointsLabel={pointsColumnLabel}
        />
        {myRow && !isMyRowVisible && <LeaderBoardCell {...myRow} />}
      </div>

      {rows.map((row, index) => {
        const isMyRow = index === myRowIndex;
        return (
          <div key={`${row.rank}-${index}`} ref={isMyRow ? myRowRef : undefined}>
            <LeaderBoardCell {...row} />
          </div>
        );
      })}

      {/* Sentinel: triggers loading the next page as it scrolls into view */}
      <div ref={sentinelRef} />
      {isFetchingNextPage && (
        <div className="flex w-full items-center justify-center py-6 text-gray-400">
          Loading more…
        </div>
      )}
    </>
  );
};
